'use client';

import DocumentCard from '../DocumentCard/DocumentCard';
import HiddenFileInput from '../HiddenFileInput/HiddenFileInput';
import type { Theme, UploadedFile, InitialFile } from '../types';

export type InlineProps = {
  dataTestId: string;
  theme: Theme;
  label: string;
  uploadButtonLabel?: string;
  helperText?: string;
  required: boolean;
  disabled: boolean;
  accept: string;
  files: UploadedFile[];
  initialFiles: InitialFile[];
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile: (index: number) => void;
  onRetryFile: (index: number) => void;
  onDocumentClick?: (fileId: string, fileName: string) => void;
  removable: boolean;
};

/**
 * Inline - Inline variant for single file upload
 *
 * @component
 * @param props - InlineProps
 * @returns Inline file upload UI
 */
const Inline = ({
  dataTestId,
  theme,
  label,
  uploadButtonLabel,
  helperText,
  required,
  disabled,
  accept,
  files,
  initialFiles: _initialFiles,
  fileInputRef,
  onInputChange,
  onRemoveFile,
  onRetryFile,
  onDocumentClick,
  removable,
}: InlineProps) => {
  const currentFile = files[0];
  const canRetry = currentFile?.status === 'error' && !currentFile.isValidationError;
  const isSuccess = currentFile?.status === 'success';
  const hasFile = Boolean(currentFile);
  const eyebrowText = isSuccess ? label : undefined;

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <>
      <DocumentCard
        dataTestId={dataTestId}
        theme={theme}
        status={currentFile?.status ?? 'empty'}
        fileName={currentFile?.file.name}
        fileSize={currentFile?.file.size}
        progress={currentFile?.progress}
        error={currentFile?.error}
        eyebrow={eyebrowText}
        label={label}
        uploadButtonLabel={uploadButtonLabel}
        helperText={helperText}
        required={required}
        disabled={disabled}
        onUpload={hasFile ? undefined : handleUploadClick}
        onRemove={removable && currentFile ? () => onRemoveFile(0) : undefined}
        onRetry={canRetry ? () => onRetryFile(0) : undefined}
        onDownload={
          isSuccess && currentFile?.url
            ? () => onDocumentClick?.(currentFile.url!, currentFile.file.name)
            : undefined
        }
        isPreExisting={currentFile?.isPreExisting}
        index={0}
      />
      <HiddenFileInput
        dataTestId={dataTestId}
        accept={accept}
        disabled={disabled}
        inputRef={fileInputRef}
        onChange={onInputChange}
      />
    </>
  );
};

Inline.displayName = 'Inline';

export default Inline;
